import React, {FC, useMemo} from 'react';

import {isHtmlElement} from '../../../../libs/shared/helpers/src/browser/domHelpers';
import {EventHandler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Layer} from '../../../../libs/shared/ui/src/components/layers/layer';
import {WidgetTypesEnum} from '../../../applications/bridge/v2/types/bridgeTypesV2';
import {
  FrontViewHandshakeModel,
  LegacyApplicationRuntimeMessageTypesEnum,
} from '../../../applications/runtime/legacyApplicationRuntimeHelpers';
import {
  buildLegacyAppEngineIframeName,
  buildLegacyAppIframeFeaturePolicies,
  buildLegacyApplicationIframeOrigin,
  buildLegacyApplicationLogger,
  buildLegacyAppViewIframeName,
  buildLegacyAppViewIframeUrl,
  legacyAppIframePermissions,
} from '../../../helpers/models/legacyApplicationHelpers';
import {
  computePluginIdFromCustomPlugin,
  onTrackIntegrationMouseEvent,
  SidePanelFrameMouseTrackingValuesEnum,
} from '../../../helpers/sidePanelTrackingHelpers';
import {LegacyApplicationModel} from '../../../models/api/legacyApplicationModel';
import {WidgetTemplateModel} from '../../../models/applications/widgetTemplateModel';
import {Iframe} from '../../common/iframe';

/*
 * Props.
 */

interface LegacyApplicationContextWidgetProps {
  application: LegacyApplicationModel;
  widget: WidgetTemplateModel;
}

/*
 * Component.
 */

export const LegacyApplicationContextWidget: FC<LegacyApplicationContextWidgetProps> = ({
  application,
  widget,
}) => {
  const {name, typeId} = application;
  const {id, type} = widget;
  const log = useMemo(() => buildLegacyApplicationLogger(name), [name]);
  const isCustomApplication = typeId.startsWith('custom-');

  /*
   * Tracking.
   */

  const onMouseTrack = (trackType: SidePanelFrameMouseTrackingValuesEnum) => {
    if (isCustomApplication) {
      onTrackIntegrationMouseEvent(trackType, application.name, {
        plugin_id: computePluginIdFromCustomPlugin(typeId),
      });
    } else {
      onTrackIntegrationMouseEvent(trackType, application.name, {
        integration_id: application.id,
      });
    }
  };

  /*
   * Iframe.
   */

  // When the iframe is loaded, send it information about the widget and engine.
  const onIframeLoad: EventHandler<HTMLIFrameElement> = ({currentTarget}) => {
    if (!currentTarget || !isHtmlElement(currentTarget) || !currentTarget.contentWindow) {
      return;
    }

    const handshakeMessage: FrontViewHandshakeModel = {
      type: LegacyApplicationRuntimeMessageTypesEnum.HANDSHAKE_VIEW,
      engineIframeName: buildLegacyAppEngineIframeName(application),
      widgetId: id,
    };

    const targetOrigin = buildLegacyApplicationIframeOrigin(application);
    currentTarget.contentWindow.postMessage(handshakeMessage, targetOrigin);
    log('View handshake sent.', handshakeMessage);
  };

  const iframeRender = (
    <Iframe
      title="Application View"
      name={buildLegacyAppViewIframeName(id)}
      endpoint={buildLegacyAppViewIframeUrl(application)}
      onLoad={onIframeLoad}
      isVisible
      sandboxPermissions={legacyAppIframePermissions}
      featurePolicies={buildLegacyAppIframeFeaturePolicies(application)}
      onMouseEnter={() => onMouseTrack(SidePanelFrameMouseTrackingValuesEnum.MOUSE_ENTER)}
      onMouseLeave={() => onMouseTrack(SidePanelFrameMouseTrackingValuesEnum.MOUSE_LEAVE)}
    />
  );

  /*
   * Type specific UI.
   */

  switch (type) {
    case WidgetTypesEnum.BLOCK:
      return iframeRender;

    case WidgetTypesEnum.LAYER:
    default:
      return <Layer>{iframeRender}</Layer>;
  }
};
